import React, { useEffect, useState } from "react";
import axios from "axios";
import { getBackendUrl } from "@/lib/backend";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Sparkles, Check, ArrowRight, Coins } from "lucide-react";

const API = `${getBackendUrl()}/api`;

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

// Round INR down to a "nice" example (nearest 1,000) so the tile
// reads clean. e.g. 20,000 stays 20,000; 20,833 rounds to 20,000.
const _roundNice = (n) => Math.max(1000, Math.floor(Number(n || 0) / 1000) * 1000);

/**
 * Pack picker — opens before the Razorpay checkout so designers consciously
 * pick the tier instead of being railroaded into whichever pack we marked
 * `highlight=true`. After a pack is selected, parent opens CheckoutDialog.
 *
 * Also includes a "Custom top-up" tile that hands off to CustomTopUpDialog
 * (triggered via `onSelectCustom`). The custom-tile rate / minimum /
 * example are hydrated from `/api/billing/custom-topup/params` — backend
 * is the single source of truth so this UI can never drift from the
 * actual rate the user is charged (previously the tile hardcoded ₹8/cr
 * while backend was ₹12/cr → user saw ₹8 in the picker but ₹12 in the
 * checkout dialog. Fixed by fetching the same endpoint the checkout
 * dialog already uses.)
 */
export default function PackPickerDialog({ open, onOpenChange, packs = [], onSelect, onSelectCustom }) {
  const [customParams, setCustomParams] = useState(null);

  // Only fetch when the dialog is opened and we don't already have
  // params — this dialog can be re-opened many times per session so
  // cache the response inside component state to avoid a request
  // storm.
  useEffect(() => {
    if (!open || customParams) return;
    let cancelled = false;
    axios
      .get(`${API}/billing/custom-topup/params`)
      .then((r) => {
        if (!cancelled) setCustomParams(r.data);
      })
      .catch(() => {
        // Silent fail — the tile still renders with a generic label
        // and the checkout dialog will show the authoritative rate.
      });
    return () => {
      cancelled = true;
    };
  }, [open, customParams]);

  // Derived display strings for the custom tile. Kept outside the JSX
  // so the logic (rate formatting, example calc) stays legible.
  const customRateLabel = customParams
    ? `₹${Number(customParams.inr_per_credit).toFixed(customParams.inr_per_credit % 1 === 0 ? 0 : 1)}`
    : null;
  const customMinLabel = customParams
    ? `₹${Number(customParams.min_amount_inr).toLocaleString("en-IN")}`
    : null;
  // Example: 4× the minimum (so the number feels aspirational without
  // being unrealistic), rounded to a nice figure. Credits are floored
  // exactly the way the backend `_credits_for` helper does it.
  const exampleAmount = customParams ? _roundNice(customParams.min_amount_inr * 4) : 0;
  const exampleCredits = customParams
    ? Math.floor(exampleAmount / customParams.inr_per_credit)
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border border-[#D4AF37]/22 text-white max-w-2xl"
        data-testid="pack-picker-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-lg tracking-tight">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            Top up your wallet
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Pick a pack. All credits stay on your account — they never expire.
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-3 gap-3 mt-2">
          {packs.map((p) => {
            const perCredit = p.credits > 0 ? p.amount / p.credits : 0;
            return (
              <button
                type="button"
                key={p.id}
                onClick={() => onSelect?.(p)}
                data-testid={`pack-card-${p.id}`}
                className={`group relative text-left rounded-2xl border p-4 transition-all bg-[#0E0E0E] hover:-translate-y-0.5 ${
                  p.highlight
                    ? "border-[#D4AF37]/55 ring-1 ring-[#D4AF37]/25 hover:border-[#D4AF37]"
                    : "border-white/10 hover:border-[#D4AF37]/40"
                }`}
              >
                {p.highlight && (
                  <span className="absolute -top-2 right-3 text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-[#D4AF37] text-black font-medium">
                    Popular
                  </span>
                )}
                <div className="text-xs uppercase tracking-[0.22em] text-white/55">
                  {p.name}
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-2xl font-display tracking-tight">
                    {formatINR(p.amount)}
                  </span>
                </div>
                <div className="mt-1 text-sm text-[#D4AF37]">
                  {p.credits.toLocaleString("en-IN")} credits
                </div>
                {p.subtitle && (
                  <div className="mt-1 text-[11px] text-white/45 leading-relaxed">
                    {p.subtitle}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-white/8 text-[10px] text-white/40 tabular-nums">
                  {perCredit > 0 ? `≈ ${formatINR(perCredit)} / credit` : ""}
                </div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="text-white/55 inline-flex items-center gap-1">
                    <Check className="w-3 h-3 text-emerald-400" />
                    Never expires
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 text-[#D4AF37] opacity-0 group-hover:opacity-100 transition" />
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-3 text-[10px] text-white/35 leading-relaxed">
          Secure checkout via Razorpay · UPI / Cards / NetBanking · GST invoice on request.
        </div>

        {onSelectCustom && (
          <button
            type="button"
            onClick={onSelectCustom}
            data-testid="pack-picker-custom-btn"
            className="mt-4 w-full text-left rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] hover:bg-[#D4AF37]/12 hover:border-[#D4AF37]/55 transition p-4 group"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center shrink-0">
                  <Coins className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium">Custom amount</div>
                  <div
                    className="text-[11px] text-white/55 mt-0.5"
                    data-testid="pack-picker-custom-subtitle"
                  >
                    {customParams ? (
                      <>
                        {customRateLabel} per credit · minimum {customMinLabel}. E.g.{" "}
                        ₹{exampleAmount.toLocaleString("en-IN")} → {exampleCredits.toLocaleString("en-IN")} credits.
                      </>
                    ) : (
                      <>Buy any number of credits at the atelier rate. Never expires.</>
                    )}
                  </div>
                </div>
              </div>
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">
                Top up <ArrowRight className="w-3 h-3" />
              </span>
            </div>
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
